import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Upload, Link as LinkIcon, X } from 'lucide-react';

const HelpRequest = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
      'video/*': ['.mp4'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10485760, // 10MB
    maxFiles: 5,
    onDrop: acceptedFiles => {
      setFiles(prev => [...prev, ...acceptedFiles]);
    }
  });

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let mediaUrls = [];
      
      // Upload files if any
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        
        const uploadResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}/upload/help-request`,
          formData
        );
        mediaUrls = uploadResponse.data.urls;
      }

      // Submit help request
      await axios.post(`${import.meta.env.VITE_API_URL}/help-requests`, {
        ...data,
        media_urls: mediaUrls,
        external_link: data.link || null
      });

      toast.success('Help request submitted successfully! We will contact you soon.');
      setFiles([]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section-padding bg-gray-50">
      <div className="container-custom max-w-3xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">We're Here to Help</h1>
          <p className="text-xl text-gray-600">
            Submit your request and our team will reach out to you
          </p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block font-semibold mb-2">Full Name *</label>
              <input 
                {...register('name', { required: 'Name is required' })} 
                className="input" 
              />
              {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block font-semibold mb-2">Email</label>
                <input 
                  type="email" 
                  {...register('email')} 
                  className="input" 
                />
              </div>
              
              <div>
                <label className="block font-semibold mb-2">Phone Number *</label>
                <input 
                  {...register('phone', { 
                    required: 'Phone is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: '10-digit phone number required'
                    }
                  })} 
                  className="input" 
                />
                {errors.phone && <span className="text-red-500 text-sm">{errors.phone.message}</span>}
              </div>
            </div>

            <div>
              <label className="block font-semibold mb-2">Type of Assistance Needed *</label>
              <select 
                {...register('type', { required: 'Please select assistance type' })} 
                className="input"
              >
                <option value="">Select...</option>
                <option value="Education Support">Education Support</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Food & Nutrition">Food & Nutrition</option>
                <option value="Shelter">Shelter</option>
                <option value="Elderly Care">Elderly Care</option>
                <option value="Emergency Assistance">Emergency Assistance</option>
                <option value="Other">Other</option>
              </select>
              {errors.type && <span className="text-red-500 text-sm">{errors.type.message}</span>}
            </div>

            <div>
              <label className="block font-semibold mb-2">Describe Your Situation *</label>
              <textarea 
                {...register('description', { 
                  required: 'Description is required',
                  minLength: { value: 20, message: 'Minimum 20 characters required' }
                })} 
                className="input" 
                rows="5"
                placeholder="Please provide details about your situation and how we can help..."
              ></textarea>
              {errors.description && <span className="text-red-500 text-sm">{errors.description.message}</span>}
            </div>

            <div>
              <label className="block font-semibold mb-2">Upload Supporting Documents/Photos</label>
              <div 
                {...getRootProps()} 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              >
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">
                  <strong>Click to upload</strong> or drag and drop
                </p>
                <p className="text-sm text-gray-500">
                  PNG, JPG, PDF, MP4 (Max 10MB, up to 5 files)
                </p>
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block font-semibold mb-2">Or Paste a Link (Google Drive, Dropbox, etc.)</label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input 
                  type="url" 
                  {...register('link')} 
                  className="input pl-10" 
                  placeholder="https://drive.google.com/..."
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading} 
              className="btn btn-primary w-full text-lg"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HelpRequest;
